import type {
  ParsedProjectPlan,
  ProjectPlanExportFormat,
  ProjectPlanV1,
  SerializedProjectPlanData,
} from './projectPlanExchange';
import { PROJECT_PLAN_FILE_LIMIT } from './safeWorkbook';

type WorkerRequest =
  | { kind: 'parse'; data: ArrayBuffer; fileName: string }
  | { kind: 'preview'; data: ArrayBuffer }
  | { kind: 'serialize'; plan: ProjectPlanV1; format: ProjectPlanExportFormat };

type WorkerResponse =
  | { ok: true; result: ParsedProjectPlan }
  | { ok: true; result: SerializedProjectPlanData }
  | { ok: true; result: string[][] }
  | { ok: false; error: string };

function runExchangeWorker<T>(request: WorkerRequest, transfer: Transferable[] = []): Promise<T> {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('Импорт и экспорт планов доступен только в браузере'));
  return new Promise<T>((resolve, reject) => {
    const worker = new Worker(new URL('./projectPlanExchange.worker.ts', import.meta.url), { type: 'module' });
    const timeout = globalThis.setTimeout(() => {
      worker.terminate();
      reject(new Error('Разбор файла плана превысил 10 секунд'));
    }, 10_000);
    const finish = (): void => {
      globalThis.clearTimeout(timeout);
      worker.terminate();
    };
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      finish();
      if (event.data.ok === true) resolve(event.data.result as T);
      else reject(new Error(event.data.error));
    };
    worker.onerror = (event) => {
      finish();
      reject(new Error(event.message || 'Ошибка Web Worker обмена планом'));
    };
    worker.postMessage(request, transfer);
  });
}

export async function parseProjectPlanFile(file: File): Promise<ParsedProjectPlan> {
  if (!(file instanceof File)) throw new Error('Не выбран файл плана');
  if (file.size > PROJECT_PLAN_FILE_LIMIT) throw new Error('Размер файла плана превышает 5 MiB');
  const data = await file.arrayBuffer();
  return runExchangeWorker<ParsedProjectPlan>({ kind: 'parse', data, fileName: file.name }, [data]);
}

export function readXlsxPreviewInWorker(data: ArrayBuffer): Promise<string[][]> {
  if (!(data instanceof ArrayBuffer)) return Promise.reject(new Error('Некорректные данные XLSX'));
  if (data.byteLength > PROJECT_PLAN_FILE_LIMIT) return Promise.reject(new Error('Размер XLSX превышает 5 MiB'));
  return runExchangeWorker<string[][]>({ kind: 'preview', data }, [data]);
}

export async function serializeProjectPlan(
  plan: ProjectPlanV1,
  format: ProjectPlanExportFormat,
): Promise<{ blob: Blob; fileName: string }> {
  const result = await runExchangeWorker<SerializedProjectPlanData>({ kind: 'serialize', plan, format });
  return { blob: new Blob([result.data], { type: result.mimeType }), fileName: result.fileName };
}
